import { DestroyRef, Injectable, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { UserInfo, UserService } from '../services/user.service';

@Injectable()
export class AuthenticatedGuard {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly authSvc = inject(AuthService);
    protected readonly router = inject(Router);
    protected readonly userSvc = inject(UserService);
    private isAuthenticated: boolean;
    private user: UserInfo;
    previousURL: string;

    constructor() {
        this.isAuthenticated = false; // Default to not authenticated.
        this.userSvc.UserSubject.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((user) => {
            this.user = user;
            this.isAuthenticated =
                user && user.AuthToken != null && user.AuthToken != '';
        });
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree {
        this.authSvc.resetIdleTimer();

        //if not logged in
        if (!this.isAuthenticated) {
            this.previousURL = route.url.join('/');
            this.router.navigate(['/login']);
            return false;
        }

        let prevURL = this.previousURL;
        this.previousURL = null;

        //if route doesn't have roles
        if (
            route.data == null ||
            route.data['roles'] == null ||
            route.data['roles'].length === 0
        ) {
            if (prevURL != null) {
                this.router.navigate(['/' + prevURL]);
            }
            return true;
        }

        //if don't have any roles
        if (this.user.RoleList == null) {
            this.router.navigate(['/unauthorized']);
            return false;
        }

        let roles: string[] = route.data['roles'];
        //if role matches what is needed
        for (let ur of this.user.RoleList) {
            if (roles.indexOf(ur) > -1) {
                if (prevURL != null) {
                    this.router.navigate(['/' + prevURL]);
                }
                return true;
            }
        }

        //else, don't have needed role
        this.router.navigate(['/unauthorized']);
        return false;
    }
}

export interface UnsavedChanges {
    canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
    changeTrackingActive: boolean;
    hasChanges: () => boolean;
    setupTracking: () => void;
    takeDownTracking: () => void;
}

@Injectable()
export class UnsavedChangesGuard {
    protected readonly unsavedSvc = inject(UnsavedChangesService);

    canDeactivate(
        target: UnsavedChanges
    ): Observable<boolean> | Promise<boolean> | boolean {
        if (!this.unsavedSvc.HasTracker()) {
            return true;
        }

        return target.canDeactivate ? target.canDeactivate() : true;
    }
}

@Injectable()
export class UnsavedChangesService implements OnInit {
    private _nativeListener: EventListenerOrEventListenerObject | null;
    private _tracker:
        | null
        | (() => Observable<boolean> | Promise<boolean> | boolean) = null;
    private _hasChanges: null | (() => boolean) = null;

    private _nativeChecker = (checker: any, event: any) => {
        const isClean = checker != null ? checker(true) : false;

        if (!isClean) {
            const msg =
                'There may be unsaved changes on this page. Do you wish to continue leaving the page?';
            event = event || window.event;

            if (event) {
                event.returnValue = msg;
            }

            return msg;
        } else {
            return null;
        }
    };

    ngOnInit() {
        this._nativeListener = null;
    }

    ClearTracking(): void {
        if (this._nativeListener) {
            window.removeEventListener('beforeunload', this._nativeListener);
            this._nativeListener = null;
        }

        this._tracker = null;
    }

    CheckTracker(): Observable<boolean> {
        const self = this;

        if (self._tracker != null) {
            const result = self._tracker();
            let obsResult: Observable<boolean>;

            if (result instanceof Promise) {
                obsResult = from(result);
            } else if (!(result instanceof Observable)) {
                obsResult = of<boolean>(result).pipe(take(1));
            } else {
                obsResult = result as Observable<boolean>;
            }

            return obsResult.pipe(take(1));
        } else {
            return of<boolean>(true).pipe(take(1));
        }
    }

    HasChanges(): boolean {
        return this._hasChanges?.() ?? false;
    }

    HasTracker(): boolean {
        return this._tracker != null;
    }

    SetTracking(
        tracker: () => Observable<boolean> | Promise<boolean> | boolean,
        hasChanges: () => boolean
    ): void {
        const self = this;

        if (self._nativeListener) {
            self.ClearTracking();
        }

        self._nativeListener = self._nativeChecker.bind(window, tracker);

        window.addEventListener('beforeunload', self._nativeListener);

        this._tracker = tracker;
        this._hasChanges = hasChanges;
    }
}
